import { useMemo } from "react";
import { useSelector } from "react-redux";
import { ProductCategory } from "@utils/constants";
import { isDiamondOrLabGrown } from "@utils/product/productValidators";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useGlobalData } from "@redux/slices/globalSlice";
import { PdpComponentNames } from "@utils/customization";

interface UsePdpVisibilityProps {
  product_type: ProductCategory;
  priceMode: number;
  itemDetail: any;
  commentsLayout: any;
  personalizationSectionLayout: any;
  supplierSectionLayout: any;
  findMatchSectionLayout: any;
  enableJewelryBuilder: boolean;
  enterSecondFlow: boolean;
  isEarring: boolean | undefined;
  visibleSections: any;
}

export const usePdpVisibility = ({
  product_type,
  priceMode,
  itemDetail,
  commentsLayout,
  personalizationSectionLayout,
  supplierSectionLayout,
  findMatchSectionLayout,
  enableJewelryBuilder,
  enterSecondFlow,
  isEarring,
  visibleSections,
}: UsePdpVisibilityProps) => {
  const userInfo = useUserInfo();
  const { owner_info = {} } = useGlobalData();

  const show_personalization_section_on_pdp_screen = useSelector(
    (state: any) =>
      state.globalData?.app_settings
        ?.show_personalization_section_on_pdp_screen ?? false
  );

  // Show comment section based on layout price modes
  const showCommentSection = useMemo(
    () => commentsLayout?.price_modes?.includes(priceMode) ?? false,
    [commentsLayout, priceMode]
  );

  // Show personalization section based on multiple conditions
  const showPersonalizationSection = useMemo(
    () =>
      visibleSections.includes(PdpComponentNames.PERSONALIZATION) &&
      (personalizationSectionLayout?.price_modes?.includes(priceMode) ??
        false) &&
      itemDetail?.custom_design_configuration &&
      show_personalization_section_on_pdp_screen &&
      (userInfo?.isGuest
        ? personalizationSectionLayout?.restrict_public_user === false
        : true),
    [
      personalizationSectionLayout,
      priceMode,
      userInfo?.isGuest,
      itemDetail?.custom_design_configuration,
      show_personalization_section_on_pdp_screen,
      visibleSections,
    ]
  );

  // Show supplier details based on layout price modes
  const showSupplierDetails = useMemo(
    () =>
      visibleSections.includes(PdpComponentNames.SUPPLIER_CONTAINER) &&
      (supplierSectionLayout?.price_modes?.includes(priceMode) ?? false),
    [visibleSections, supplierSectionLayout, priceMode]
  );

  // Check if product matches price mode for find match functionality
  const matchesPriceMode = useMemo(
    () =>
      isDiamondOrLabGrown(product_type) &&
      (findMatchSectionLayout?.price_modes?.includes(priceMode) ?? false),
    [product_type, findMatchSectionLayout, priceMode]
  );

  // Show match pair button based on jewelry builder and earring logic
  const shouldShowMatchPairButton = useMemo(
    () =>
      enableJewelryBuilder && enterSecondFlow === true
        ? !(isEarring ?? false) && matchesPriceMode
        : matchesPriceMode,
    [enableJewelryBuilder, enterSecondFlow, isEarring, matchesPriceMode]
  );

  // Show owner info based on user settings and product configuration
  const showOwnerInfo = useMemo(
    () =>
      userInfo?.user?.owner_info?.show_owner_info === true &&
      owner_info?.products?.includes?.(product_type) &&
      owner_info?.price_modes?.includes?.(priceMode),
    [
      userInfo?.user?.owner_info?.show_owner_info,
      owner_info,
      product_type,
      priceMode,
    ]
  );

  return {
    showCommentSection,
    showPersonalizationSection,
    showSupplierDetails,
    shouldShowMatchPairButton,
    showOwnerInfo,
  };
};
